import { useEffect, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import {
  type Handoff,
  type HandoffSeverity,
  ackHandoff,
  deleteHandoff,
  getHandoff,
  upsertHandoff,
} from "@/api/handoffs";

type Props = {
  open: boolean;
  assignmentId: string | null;
  canAuthor: boolean;
  onClose: () => void;
};

const SEVERITIES: HandoffSeverity[] = ["NORMAL", "WARNING", "CRITICAL"];

function severityColor(s: HandoffSeverity): "default" | "warning" | "error" {
  if (s === "WARNING") return "warning";
  if (s === "CRITICAL") return "error";
  return "default";
}

export function HandoffDrawer({ open, assignmentId, canAuthor, onClose }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const [body, setBody] = useState("");
  const [severity, setSeverity] = useState<HandoffSeverity>("NORMAL");
  const [editing, setEditing] = useState(false);

  const handoffQuery = useQuery({
    queryKey: ["handoff", assignmentId],
    queryFn: () => (assignmentId ? getHandoff(assignmentId) : Promise.resolve(null)),
    enabled: open && Boolean(assignmentId),
  });

  useEffect(() => {
    if (handoffQuery.data) {
      setBody(handoffQuery.data.body);
      setSeverity(handoffQuery.data.severity);
      setEditing(false);
    } else if (open && handoffQuery.isSuccess) {
      setBody("");
      setSeverity("NORMAL");
      setEditing(canAuthor);
    }
  }, [handoffQuery.data, handoffQuery.isSuccess, open, canAuthor]);

  const saveMutation = useMutation({
    mutationFn: () =>
      assignmentId
        ? upsertHandoff(assignmentId, body, severity)
        : Promise.reject(new Error("no_assignment")),
    onSuccess: (h: Handoff) => {
      qc.invalidateQueries({ queryKey: ["handoff", assignmentId] });
      qc.invalidateQueries({ queryKey: ["pending-handoffs"] });
      setEditing(false);
      handoffQuery.refetch();
      void h;
    },
  });

  const ackMutation = useMutation({
    mutationFn: () =>
      handoffQuery.data
        ? ackHandoff(handoffQuery.data.id)
        : Promise.reject(new Error("no_handoff")),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["handoff", assignmentId] });
      qc.invalidateQueries({ queryKey: ["pending-handoffs"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () =>
      assignmentId
        ? deleteHandoff(assignmentId)
        : Promise.reject(new Error("no_assignment")),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["handoff", assignmentId] });
      qc.invalidateQueries({ queryKey: ["pending-handoffs"] });
      setBody("");
      setSeverity("NORMAL");
      setEditing(canAuthor);
    },
  });

  const h = handoffQuery.data;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: { xs: "100%", sm: 520 } } } }}
    >
      <Box sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column" }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {t("handoff.title")}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("handoff.subtitle")}
        </Typography>

        {handoffQuery.isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {!handoffQuery.isLoading && !editing && h && (
          <Stack spacing={2} sx={{ flex: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                label={t(`handoff.severity.${h.severity}`)}
                color={severityColor(h.severity)}
                size="small"
              />
              <Typography variant="caption" color="text.secondary">
                {new Date(h.updated_at).toLocaleString()}
              </Typography>
              <Chip
                size="small"
                variant="outlined"
                label={t("handoff.ackCount", { count: h.ack_count })}
              />
            </Stack>
            <Box
              sx={{
                p: 2,
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                whiteSpace: "pre-wrap",
                fontFamily: "inherit",
                bgcolor: "background.default",
              }}
            >
              {h.body}
            </Box>
            {!h.acknowledged_by_me && (
              <Button
                variant="contained"
                onClick={() => ackMutation.mutate()}
                disabled={ackMutation.isPending}
              >
                {t("handoff.ack")}
              </Button>
            )}
            {h.acknowledged_by_me && (
              <Alert severity="success">{t("handoff.acked")}</Alert>
            )}
            {canAuthor && (
              <Stack direction="row" spacing={1}>
                <Button variant="outlined" onClick={() => setEditing(true)}>
                  {t("common.edit") ?? "Modifier"}
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => deleteMutation.mutate()}
                  disabled={deleteMutation.isPending}
                >
                  {t("handoff.delete")}
                </Button>
              </Stack>
            )}
          </Stack>
        )}

        {!handoffQuery.isLoading && !editing && !h && (
          <Alert severity="info">{t("handoff.empty")}</Alert>
        )}

        {editing && canAuthor && (
          <Stack spacing={2} sx={{ flex: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>{t("handoff.severityLabel")}</InputLabel>
              <Select
                label={t("handoff.severityLabel")}
                value={severity}
                onChange={(e) => setSeverity(e.target.value as HandoffSeverity)}
              >
                {SEVERITIES.map((s) => (
                  <MenuItem key={s} value={s}>
                    {t(`handoff.severity.${s}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              multiline
              minRows={8}
              maxRows={20}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={t("handoff.bodyPlaceholder")}
              inputProps={{ maxLength: 4000 }}
            />
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              {h && (
                <Button onClick={() => setEditing(false)} disabled={saveMutation.isPending}>
                  {t("common.cancel")}
                </Button>
              )}
              <Button
                variant="contained"
                disabled={saveMutation.isPending || body.trim().length === 0}
                onClick={() => saveMutation.mutate()}
              >
                {t("common.save")}
              </Button>
            </Stack>
          </Stack>
        )}
      </Box>
    </Drawer>
  );
}
